import { defineComponent as _defineComponent } from 'vue';
import FullCalendar, { EventApi, } from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import luxon2Plugin, { toLuxonDateTime } from '@fullcalendar/luxon2';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { getCurrentInstance, onMounted, ref, watch, } from 'vue';
import { ForecastType } from '../Services/WeatherForecastService';
import { DateTime } from 'luxon';
import ProgressSpinner from 'primevue/progressspinner';
import OverlayPanel from 'primevue/overlaypanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { useAxios } from '../Plugins/axios';
import { useRoute } from '../Plugins/ziggy';
import _axios from 'axios';
import { useMitt } from '../Plugins/mitt';
import { isLoaded, trans, transChoice } from 'laravel-vue-i18n';
import itLocale from '@fullcalendar/core/locales/it';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { formatISODate, waitForComponent } from '../Services/Utils';
export default /*#__PURE__*/ _defineComponent({
    __name: 'Calendar',
    props: {
        events: {
            type: Array,
            required: true,
        },
        showWeather: {
            type: Boolean,
            default: true,
        },
        orchard: {
            type: Object,
            default: {},
        },
        phenologicalPhases: {
            type: Array,
            default: [],
        },
    },
    setup(__props, { expose }) {
        expose();
        const props = __props;
        const app = getCurrentInstance();
        const locale = app?.appContext.config.globalProperties.$locale ?? 'it';
        const axios = useAxios();
        const route = useRoute();
        const emitter = useMitt();
        const visible = ref(true);
        const spinner = ref();
        const waterRequirementsPopup = ref();
        const waterRequirementsPopupData = ref();
        const nutritionBalancePopup = ref();
        const nutritionBalancePopupData = ref();
        let spinnerEl = null;
        let loadingEvents = false;
        const calendar = ref();
        const calendarLocales = {
            it: itLocale,
            en: enLocale,
        };
        const viewType = ref(null);
        const forecastWeatherEvents = ref({
            daily: [],
            hourly: [],
        });
        const weatherForecastLoadTime = ref(null);
        const historicalWeatherEvents = ref({
            daily: [],
            hourly: [],
        });
        const dataEvents = ref([]);
        const waterRequirements = ref([]);
        const calendarDataCache = ref({});
        let calendarDataCancelToken;
        const onEventsLoading = (isLoading) => {
            loadingEvents = isLoading ?? loadingEvents;
            if (spinnerEl) {
                if (loadingEvents) {
                    spinnerEl.classList.remove('hidden');
                }
                else {
                    spinnerEl.classList.add('hidden');
                }
            }
        };
        const options = {
            locale: calendarLocales[locale] ?? itLocale,
            firstDay: 1,
            plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, luxon2Plugin],
            headerToolbar: {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek',
            },
            buttonText: {
                month: trans('labels.month_view'),
                week: trans('labels.hourly_weather'),
            },
            dayMaxEvents: 8,
            eventTextColor: '#000',
            eventOrder: 'phenologicalPhase,weather,waterRequirements,start,-duration,allDay,title',
            eventOrderStrict: true,
            eventOverlap: false,
            eventSources: [
                (fetchInfo, successCallback, failureCallback) => {
                    successCallback([]);
                    loadCalendarData(DateTime.fromJSDate(fetchInfo.start), DateTime.fromJSDate(fetchInfo.end)).then((data) => {
                        if (!data) {
                            return;
                        }
                        historicalWeatherEvents.value = data.historicalWeatherEvents;
                        forecastWeatherEvents.value = data.weatherForecastEvents;
                        waterRequirements.value = data.waterRequirements;
                        dataEvents.value = data.dataEvents;
                        refetchEvents('historicalWeatherEvents');
                        refetchEvents('forecastWeatherEvents');
                        refetchEvents('dataEvents');
                    });
                },
                {
                    id: 'historicalWeatherEvents',
                    events: (fetchInfo, successCallback, failureCallback) => {
                        const events = getWeatherEvents(historicalWeatherEvents.value);
                        successCallback(events);
                    },
                },
                {
                    id: 'forecastWeatherEvents',
                    events: (fetchInfo, successCallback, failureCallback) => {
                        const events = getWeatherEvents(forecastWeatherEvents.value);
                        successCallback(events);
                    },
                },
                {
                    id: 'dataEvents',
                    events: (fetchInfo, successCallback, failureCallback) => {
                        const events = dataEvents.value;
                        successCallback(events);
                    },
                },
                {
                    id: 'phenologicalPhasesEvents',
                    events: (fetchInfo, successCallback, failureCallback) => {
                        successCallback(getPhenologicalPhasesEvents(fetchInfo));
                    },
                },
            ],
            viewClassNames: (arg) => {
                viewType.value = arg.view.type;
                return [];
            },
            allDayContent: '',
            slotDuration: '01:00:00',
        };
        onMounted(() => {
            addSpinnerToCalendarHeader();
        });
        watch(viewType, (viewType, oldViewType) => {
            refetchEvents('historicalWeatherEvents');
            refetchEvents('forecastWeatherEvents');
        });
        const addSpinnerToCalendarHeader = async () => {
            const cal = await waitForComponent(calendar);
            const calendarEl = cal.$el;
            const calendarTitleWrapper = calendarEl.getElementsByClassName('fc-toolbar-chunk')[1];
            if (spinner.value.$el) {
                spinnerEl = spinner.value.$el;
                calendarTitleWrapper.prepend(spinnerEl);
                onEventsLoading();
            }
        };
        const getWeatherEvents = (events) => {
            if (!events) {
                return [];
            }
            const displayedEvents = [];
            // add daily weather events to displayed events
            if (Array.isArray(events.daily)) {
                displayedEvents.push(...events.daily);
            }
            // add water requirement data to daily events
            displayedEvents.forEach((event) => {
                const waterRequirementsIndex = Object.keys(waterRequirements.value).find((date) => {
                    const waterRequirementDate = DateTime.fromISO(date, { zone: 'utc' });
                    const eventDate = DateTime.fromFormat(event.start, 'yyyy-MM-dd', {
                        zone: 'utc',
                    });
                    return waterRequirementDate.equals(eventDate);
                });
                if (waterRequirementsIndex) {
                    event.waterRequirements = waterRequirements.value[waterRequirementsIndex];
                    const hasWaterRequirementProblem = event.waterRequirements.filter((wr) => wr.water_volume >= 190).length >
                        0;
                    if (hasWaterRequirementProblem) {
                        event.classNames += ' water-requirement-problem';
                    }
                }
            });
            // add hourly weather events to displayed events if view is timeGridWeek or timeGridDay
            if (Array.isArray(events.hourly) &&
                ['timeGridWeek', 'timeGridDay'].includes(viewType.value || '')) {
                return displayedEvents.concat(events.hourly);
            }
            return displayedEvents;
        };
        const refetchEvents = (eventSourceId) => {
            const eventSource = calendar.value
                ?.getApi()
                .getEventSourceById(eventSourceId);
            eventSource.refetch();
        };
        const getPhenologicalPhasesEvents = (fetchInfo) => {
            const formatEvents = (year) => props.phenologicalPhases.map((phase) => {
                const start = DateTime.fromFormat(phase.start_day.toString(), 'Mdd').set({
                    year,
                });
                let end = DateTime.fromFormat(phase.end_day.toString(), 'Mdd')
                    .plus({ days: 1 })
                    .set({ year });
                if (start > end) {
                    end = end.plus({ years: 1 });
                }
                return {
                    title: trans(`phenological_phases.${phase.phenological_phase.code}`),
                    start: start.toISODate(),
                    end: end.toISODate(),
                    allDay: true,
                    classNames: `phenological-phase phenological-phase-${phase.phenological_phase.code}`,
                    phenologicalPhase: {},
                };
            });
            const calendarYear = DateTime.fromJSDate(fetchInfo.start).year;
            const events = formatEvents(calendarYear - 1).concat(formatEvents(calendarYear));
            return events;
        };
        const loadCalendarData = async (start, end) => {
            const withForecast = needLoadWeatherForecast();
            const cacheKey = `${start.toISO()}-${end.toISO()}`;
            if (calendarDataCancelToken) {
                calendarDataCancelToken.cancel();
            }
            calendarDataCancelToken = _axios.CancelToken.source();
            onEventsLoading(true);
            if (calendarDataCache.value[cacheKey]) {
                const cacheLastUpdate = calendarDataCache.value[cacheKey].lastUpdate;
                const cacheExpired = DateTime.now().diff(cacheLastUpdate, 'hours').hours >= 1;
                // If now is not between start and end (old data) or
                // if now is between start and end (current data) and cache is not expired
                // return cached data
                if ((start < DateTime.now() && end < DateTime.now()) ||
                    (start <= DateTime.now() && end >= DateTime.now() && !cacheExpired)) {
                    onEventsLoading(false);
                    return calendarDataCache.value[cacheKey].data;
                }
            }
            return axios
                .get(route('api.calendar-data'), {
                params: {
                    orchard_id: props.orchard.id,
                    from: start.toISO(),
                    to: end.toISO(),
                    with_forecast: withForecast,
                },
                cancelToken: calendarDataCancelToken.token,
            })
                .then((res) => {
                if (!res) {
                    return null;
                }
                onEventsLoading(false);
                const data = res.data.data;
                const weatherForecastEvents = getWeatherForecastEvents(data.weatherForecast);
                const historicalWeatherEvents = getHistoricalWeatherEvents(data.historicalWeather);
                const waterRequirements = data.waterRequirements.reduce((acc, curr) => {
                    acc[curr.date] = acc[curr.date] || [];
                    acc[curr.date].push(curr);
                    return acc;
                }, Object.create(null));
                const irrigationEvents = getDataEvents(data.irrigations, trans('panels.IrrigationDetails.title'), 'irrigation');
                const fertilizationEvents = getDataEvents(data.fertilizations, trans('panels.FertilizationDetails.title'), 'fertilization');
                const irrigationWaterDataEvents = getDataEvents(data.irrigationWaterData, trans('panels.IrrigationWaterDataDetails.title'), 'irrigation-water-data');
                const leavesDataEvents = getDataEvents(data.leavesData, trans('panels.LeavesDataDetails.title'), 'leaves-data');
                const soilDataEvents = getDataEvents(data.soilData, trans('panels.SoilDataDetails.title'), 'soil-data');
                const symptomRecordEvents = getDataEvents(data.symptomRecords, transChoice('labels.symptomatology', 1), 'symptoms');
                const dataEvents = [
                    ...irrigationEvents,
                    ...fertilizationEvents,
                    ...irrigationWaterDataEvents,
                    ...leavesDataEvents,
                    ...soilDataEvents,
                    ...symptomRecordEvents,
                    ...data.nutritionBalances,
                ];
                const calendarData = {
                    historicalWeatherEvents,
                    waterRequirements,
                    weatherForecastEvents,
                    dataEvents,
                };
                calendarDataCache.value[cacheKey] = {
                    data: calendarData,
                    lastUpdate: DateTime.now(),
                };
                return calendarData;
            });
        };
        const needLoadWeatherForecast = () => {
            const now = DateTime.now().startOf('hour');
            return (!weatherForecastLoadTime.value ||
                weatherForecastLoadTime.value.diff(now, 'hour').hours > 1);
        };
        const getWeatherForecastEvents = (data) => {
            // If no data return cached data
            if (!data) {
                return forecastWeatherEvents.value;
            }
            const weatherForecastEvents = {
                daily: data.daily.map((data) => weatherForecastDataToEvent(data, ForecastType.Daily)),
                hourly: data.hourly.map((data) => weatherForecastDataToEvent(data, ForecastType.Hourly)),
            };
            weatherForecastLoadTime.value = DateTime.now().startOf('hour');
            return weatherForecastEvents;
        };
        const weatherForecastDataToEvent = (forecastData, type) => {
            const date = DateTime.fromSeconds(forecastData.time);
            return getEventObject(trans('labels.weather'), date, type === ForecastType.Daily ? true : false, `weather-event weather-forecast-event weather-event-${type}`, {
                forecastType: type,
                weather: forecastData,
            });
        };
        const getHistoricalWeatherEvents = (data) => {
            // If no data return cached data
            if (!data) {
                return {
                    daily: [],
                    hourly: [],
                };
            }
            const historicalWeatherEvents = {
                daily: data.daily.map((data) => historicalWeatherDataToEvent(data, ForecastType.Daily)),
                hourly: data.hourly.map((data) => historicalWeatherDataToEvent(data, ForecastType.Hourly)),
            };
            return historicalWeatherEvents;
        };
        const historicalWeatherDataToEvent = (forecastData, type) => {
            let start = DateTime.fromISO(forecastData.date).startOf('day');
            if (forecastData.time) {
                const time = DateTime.fromFormat(forecastData.time, 'HH:mm:ss');
                start = start.set({
                    hour: time.hour,
                    minute: time.minute,
                    second: time.second,
                });
            }
            const startString = type === ForecastType.Daily ? start.toISODate() : start.toISO();
            const endString = type === ForecastType.Daily
                ? startString
                : start.plus({ minutes: 1 }).toISO();
            // map historical weather data to weather forecast data
            forecastData = {
                ...forecastData,
                temperature: forecastData.temperature_avg,
                humidity: forecastData.relative_humidity,
            };
            return {
                start: startString,
                end: endString,
                weather: forecastData,
                forecastType: type,
                editable: false,
                classNames: `weather-event weather-event-${type}`,
            };
        };
        const getDataEvents = (data, label, type) => {
            return data.map((data) => {
                const date = DateTime.fromISO(data.date, {
                    zone: 'utc',
                });
                return getEventObject(label, date, true, `event-marker ${type}`, {
                    dataType: type,
                    data,
                });
            });
        };
        const toggleWaterRequirementsPopup = (event, arg) => {
            waterRequirementsPopupData.value = arg.extendedProps.waterRequirements;
            waterRequirementsPopup.value?.toggle(event);
        };
        const getEventObject = (title, date, allDay = false, classNames = '', additionalData = {}) => {
            return {
                title,
                start: allDay ? date.toISODate() : date.toISO(),
                end: allDay ? date.toISO() : date.plus({ minutes: 1 }).toISO(),
                allDay,
                editable: false,
                classNames,
                ...additionalData,
            };
        };
        const openDataEventDetails = (type, data, event) => {
            switch (type) {
                case 'irrigation':
                    emitter.emit('open-irrigation-details', data);
                    break;
                case 'fertilization':
                    emitter.emit('open-fertilization-details', data);
                    break;
                case 'irrigation-water-data':
                    emitter.emit('open-irrigation-water-data-details', data);
                    break;
                case 'leaves-data':
                    emitter.emit('open-leaves-data-details', data);
                    break;
                case 'soil-data':
                    emitter.emit('open-soil-data-details', data);
                    break;
                case 'symptoms':
                    emitter.emit('open-symptom-record-details', data);
                    break;
                case 'nutrition-balance':
                    nutritionBalancePopupData.value = data;
                    nutritionBalancePopup.value?.toggle(event);
                    break;
                default:
                    break;
            }
        };
        const openNutritionBalanceDetails = (data) => emitter.emit('open-nutrition-balance-details', data);
        const __returned__ = { app, locale, axios, route, emitter, props, visible, spinner, waterRequirementsPopup, waterRequirementsPopupData, nutritionBalancePopup, nutritionBalancePopupData, get spinnerEl() { return spinnerEl; }, set spinnerEl(v) { spinnerEl = v; }, get loadingEvents() { return loadingEvents; }, set loadingEvents(v) { loadingEvents = v; }, calendar, calendarLocales, viewType, forecastWeatherEvents, weatherForecastLoadTime, historicalWeatherEvents, dataEvents, waterRequirements, calendarDataCache, get calendarDataCancelToken() { return calendarDataCancelToken; }, set calendarDataCancelToken(v) { calendarDataCancelToken = v; }, onEventsLoading, options, addSpinnerToCalendarHeader, getWeatherEvents, refetchEvents, getPhenologicalPhasesEvents, loadCalendarData, needLoadWeatherForecast, getWeatherForecastEvents, weatherForecastDataToEvent, getHistoricalWeatherEvents, historicalWeatherDataToEvent, getDataEvents, toggleWaterRequirementsPopup, getEventObject, openDataEventDetails, openNutritionBalanceDetails, get FullCalendar() { return FullCalendar; }, get toLuxonDateTime() { return toLuxonDateTime; }, get ProgressSpinner() { return ProgressSpinner; }, get OverlayPanel() { return OverlayPanel; }, get DataTable() { return DataTable; }, get Column() { return Column; }, get trans() { return trans; }, get transChoice() { return transChoice; }, get formatISODate() { return formatISODate; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
